import { useEffect, useState } from "react";
import {
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  Typography,
  Box,
  TextField,
  CardContent,
  Button,
  LinearProgress,
  Alert,
  Tooltip,
} from "@mui/material";
import { Card } from "@mui/material";
import { styled } from "@mui/system";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { getAdReporting } from "../../Utils/RestUtils";
import InfoIcon from "@mui/icons-material/Info";

const ExpandIcon = styled("span")(({ expanded }) => ({
  display: "inline-block",
  transition: "transform 0.2s ease",
  transform: expanded ? "rotate(120deg)" : "rotate(0deg)",
  marginRight: "8px",
}));

const AnimatedRow = styled(TableRow)(({ expanded }) => ({
  transition: "all 0.2s ease",
  height: expanded ? "auto" : "40px",
  overflow: "hidden",
}));

export default function CampaignStatsTable() {
  const [reportResponse, setReportResponse] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  const [fromDate, setFromDate] = useState(
    new Date(
      Date.UTC(
        yesterday.getFullYear(),
        yesterday.getMonth(),
        yesterday.getDate(),
        0,
        0,
        0,
        999
      )
    )
  );
  const [toDate, setToDate] = useState(
    new Date(
      Date.UTC(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        23,
        59,
        59,
        999
      )
    )
  );

  async function loadReportData() {
    setIsLoading(true);
    const response = await getAdReporting(fromDate, toDate);
    setReportResponse(response);
    setIsLoading(false);
  }

  useEffect(() => {
    loadReportData();
  }, []);

  async function handleFromDateChange(selectedDate) {
    if (!selectedDate?.isValid()) return;
    const newDate = selectedDate.toDate();
    setFromDate(
      new Date(
        Date.UTC(
          newDate.getFullYear(),
          newDate.getMonth(),
          newDate.getDate(),
          0,
          0,
          0,
          999
        )
      )
    );
  }

  async function handleToDateChange(selectedDate) {
    if (!selectedDate?.isValid()) return;
    const newDate = selectedDate.toDate();
    setToDate(
      new Date(
        Date.UTC(
          newDate.getFullYear(),
          newDate.getMonth(),
          newDate.getDate(),
          23,
          59,
          59,
          999
        )
      )
    );
  }

  const [expanded, setExpanded] = useState({});

  const toggleExpand = (key) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const getHoursSpent = (data) => {
    let result =
      ((60 / (6 / 5)) * data.salvage_quotes +
        (60 / (6 / 0.5)) * data.quotes_count) /
      10 /
      60;

    return result === 0 ? 0 : result.toFixed(4);
  };

  const getMarginOnCosts = (data) => {
    if (getRevenue(data) === 0 || data.cost === 0) {
      return 0;
    }

    return (
      ((getRevenue(data) - data.cost.toFixed(2)) / data.cost.toFixed(2)) *
      100
    ).toFixed(2);
  };

  const getScrapConversion = (data) => {
    if (data.quotes_count === 0 || data.converted_scrap === 0) {
      return 0;
    }

    return ((data.converted_scrap / data.quotes_count) * 100).toFixed(2);
  };

  const getSalvageConversion = (data) => {
    if (data.quotes_count === 0 || data.converted_salvage === 0) {
      return 0;
    }

    return ((data.converted_salvage / data.quotes_count) * 100).toFixed(2);
  };

  const getScrapQuotePercentage = (data) => {
    if (data.quotes_count === 0 || data.scrap_quotes === 0) {
      return 0;
    }

    return ((data.scrap_quotes / data.quotes_count) * 100).toFixed(2);
  };

  const getRevenue = (data) => {
    return (data.converted_salvage * 350 + data.scrap_commission).toFixed(2);
  };

  const groupedData = reportResponse.reduce((acc, item) => {
    const sourceKey = `${item.website}-${item.provider}`;
    if (!acc[sourceKey]) {
      acc[sourceKey] = {
        website: item.website,
        provider: item.provider,
        first_form_quotes: 0,
        second_form_quotes: 0,
        total_clicks: 0,
        cost: 0,
        campaigns: {},
        quotes_count: 0,
        converted_salvage: 0,
        converted_scrap: 0,
        salvage_quotes: 0,
        no_cover_quotes: 0,
        scrap_quotes: 0,
        total_impressions: 0,
        scrap_commission: 0,
      };
    }

    acc[sourceKey].first_form_quotes += Number(item.first_form_quotes);
    acc[sourceKey].second_form_quotes += Number(item.second_form_quotes);
    acc[sourceKey].total_clicks += Number(item.total_clicks);
    acc[sourceKey].quotes_count += Number(item.quotes_count);
    acc[sourceKey].converted_salvage += Number(item.converted_salvage);
    acc[sourceKey].converted_scrap += Number(item.converted_scrap);
    acc[sourceKey].salvage_quotes += Number(item.salvage_quotes);
    acc[sourceKey].no_cover_quotes += Number(item.no_cover_quotes);
    acc[sourceKey].scrap_quotes += Number(item.scrap_quotes);
    acc[sourceKey].total_impressions += Number(item.total_impressions);
    acc[sourceKey].scrap_commission += Number(item.scrap_commission);
    acc[sourceKey].cost += Number(item.cost);

    const campaignKey = item.campaign;
    if (!acc[sourceKey].campaigns[campaignKey]) {
      acc[sourceKey].campaigns[campaignKey] = {
        campaign_name: item.campaign,
        first_form_quotes: 0,
        second_form_quotes: 0,
        total_clicks: 0,
        cost: 0,
        ad_groups: [],
        quotes_count: 0,
        converted_salvage: 0,
        converted_scrap: 0,
        salvage_quotes: 0,
        no_cover_quotes: 0,
        scrap_quotes: 0,
        total_impressions: 0,
        scrap_commission: 0,
      };
    }

    acc[sourceKey].campaigns[campaignKey].first_form_quotes += Number(
      item.first_form_quotes
    );
    acc[sourceKey].campaigns[campaignKey].second_form_quotes += Number(
      item.second_form_quotes
    );
    acc[sourceKey].campaigns[campaignKey].total_clicks += Number(
      item.total_clicks
    );
    acc[sourceKey].campaigns[campaignKey].cost += Number(item.cost);
    acc[sourceKey].campaigns[campaignKey].quotes_count += Number(
      item.quotes_count
    );
    acc[sourceKey].campaigns[campaignKey].converted_salvage += Number(
      item.converted_salvage
    );
    acc[sourceKey].campaigns[campaignKey].converted_scrap += Number(
      item.converted_scrap
    );
    acc[sourceKey].campaigns[campaignKey].salvage_quotes += Number(
      item.salvage_quotes
    );
    acc[sourceKey].campaigns[campaignKey].no_cover_quotes += Number(
      item.no_cover_quotes
    );
    acc[sourceKey].campaigns[campaignKey].scrap_quotes += Number(
      item.scrap_quotes
    );
    acc[sourceKey].campaigns[campaignKey].total_impressions += Number(
      item.total_impressions
    );
    acc[sourceKey].campaigns[campaignKey].scrap_commission += Number(
      item.scrap_commission
    );

    acc[sourceKey].campaigns[campaignKey].ad_groups.push(item);

    return acc;
  }, {});

  const calculateTotals = (groupedData) => {
    const totals = {
      first_form_quotes: 0,
      second_form_quotes: 0,
      total_clicks: 0,
      cost: 0,
      quotes_count: 0,
      converted_salvage: 0,
      converted_scrap: 0,
      salvage_quotes: 0,
      no_cover_quotes: 0,
      scrap_quotes: 0,
      total_impressions: 0,
      scrap_commission: 0,
    };

    Object.values(groupedData).forEach((sourceData) => {
      totals.first_form_quotes += sourceData.first_form_quotes;
      totals.second_form_quotes += sourceData.second_form_quotes;
      totals.total_clicks += sourceData.total_clicks;
      totals.cost += sourceData.cost;
      totals.quotes_count += sourceData.quotes_count;
      totals.converted_salvage += sourceData.converted_salvage;
      totals.converted_scrap += sourceData.converted_scrap;
      totals.salvage_quotes += sourceData.salvage_quotes;
      totals.no_cover_quotes += sourceData.no_cover_quotes;
      totals.scrap_quotes += sourceData.scrap_quotes;
      totals.total_impressions += sourceData.total_impressions;
      totals.scrap_commission += sourceData.scrap_commission;
    });

    return totals;
  };

  const totals = calculateTotals(groupedData);

  const handleMainLevelClick = (key) => {
    // Close all nested levels when the main level is closed
    if (expanded[key]) {
      setExpanded({});
    } else {
      toggleExpand(key);
    }
  };

  return (
    <CardContent>
      <Typography typography="h4" component="h1" mb={2}>
        Reporting
      </Typography>
      <Typography typography="h5" component="h1" mb={2}>
        Please select a date
      </Typography>
      {
        <Box
          sx={{
            display: "flex",
            marginTop: "10px",
            marginBottom: "10px",
            gap: "1rem",
          }}
        >
          <Box>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                inputFormat="DD/MM/YYYY"
                label="From"
                value={dayjs(fromDate)}
                onChange={handleFromDateChange}
                renderInput={(params) => <TextField {...params} />}
              />
            </LocalizationProvider>
          </Box>

          <Box>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                inputFormat="DD/MM/YYYY"
                minDate={fromDate}
                maxDate={
                  new Date(
                    new Date().getFullYear(),
                    new Date().getMonth() + 1,
                    0
                  )
                }
                label="From"
                value={dayjs(toDate)}
                onChange={handleToDateChange}
                renderInput={(params) => <TextField {...params} />}
              />
            </LocalizationProvider>
          </Box>
          <Box>
            <Button
              disabled={isLoading}
              onClick={() => {
                loadReportData();
              }}
            >
              Search
            </Button>
          </Box>
        </Box>
      }

      <Card style={{ padding: "20px" }}>
        {isLoading ? (
          <Box sx={{ width: "100%" }}>
            <LinearProgress />
          </Box>
        ) : reportResponse.length ? (
          <Table sx={{ width: "2000px" }}>
            <TableHead>
              <TableRow>
                <TableCell style={{ width: "5%" }}>
                  Site/Campaign/Group
                </TableCell>
                <TableCell style={{ width: "5%" }}>1st Form Fills</TableCell>
                <TableCell style={{ width: "5%" }}>
                  1st to 2nd Form Fill Rate
                </TableCell>
                <TableCell style={{ width: "5%" }}>
                  <Tooltip title="Cost for lead generation">
                    <span>
                      Lead Gen Cost
                      <InfoIcon fontSize="small" />
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell style={{ width: "5%" }}>
                  <Tooltip title="This is scrap income and gross profit from auction sales. Calculation: (Sale price - Purchase price - Auction fees) - VAT margin if profit made.">
                    <span>
                      Revenue
                      <InfoIcon fontSize="small" />
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell style={{ width: "5%" }}>
                  <Tooltip title="Revenue (Scrap income and gross profit from auction sales) - Lead Generation Costs.">
                    <span>
                      Revenue after Lead Gen
                      <InfoIcon fontSize="small" />
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell style={{ width: "5%" }}>
                  <Tooltip title="This is the percentage margin made against the Lead Gen costs. Calculated: Revenue after lead gen / Lead gen costs.">
                    <span>
                      Margin % on costs
                      <InfoIcon fontSize="small" />
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell style={{ width: "5%" }}>
                  <Tooltip title="Revenue per &pound; spent on Lead Generation before lead gen costs removed">
                    <span>
                      Revenue / £
                      <InfoIcon fontSize="small" />
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell style={{ width: "5%" }}>Revenue / Click</TableCell>
                <TableCell style={{ width: "5%" }}>Revenue / Quote</TableCell>
                <TableCell style={{ width: "5%" }}>Clicks</TableCell>
                <TableCell style={{ width: "5%" }}>
                  Search impr. share
                </TableCell>
                <TableCell style={{ width: "5%" }}>Quotes</TableCell>
                <TableCell style={{ width: "5%" }}>Clicks to Quotes</TableCell>
                <TableCell style={{ width: "5%" }}>Total Converted</TableCell>
                <TableCell style={{ width: "5%" }}>
                  Conversion Rate / Click
                </TableCell>
                <TableCell style={{ width: "5%" }}>
                  Conversion Rate / Quote
                </TableCell>
                <TableCell style={{ width: "5%" }}>
                  Salvage Percentage
                </TableCell>
                <TableCell style={{ width: "5%" }}>
                  No Coverage Percentage
                </TableCell>
                <TableCell style={{ width: "5%" }}>
                  Scrap Quote Percentage
                </TableCell>
                <TableCell style={{ width: "5%" }}>Hours spent</TableCell>
                <TableCell style={{ width: "5%" }}>Per Hour Earning</TableCell>
                <TableCell style={{ width: "5%" }}>Salvage Quotes</TableCell>
                <TableCell style={{ width: "5%" }}>No Cover Quotes</TableCell>
                <TableCell style={{ width: "5%" }}>Converted Scrap</TableCell>
                <TableCell style={{ width: "5%" }}>Scrap Conversion</TableCell>
                <TableCell style={{ width: "5%" }}>Converted Salvage</TableCell>
                <TableCell style={{ width: "5%" }}>
                  Salvage conversion
                </TableCell>
                <TableCell style={{ width: "5%" }}>Impressions</TableCell>
                <TableCell style={{ width: "5%" }}>Scrap Commission</TableCell>
                <TableCell style={{ width: "5%" }}>Revenue Salvage</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(groupedData).map(([sourceKey, sourceData]) => (
                <>
                  {/* Website + Provider Level */}
                  <AnimatedRow
                    key={sourceKey}
                    expanded={expanded[sourceKey]}
                    style={{ backgroundColor: "#fff", cursor: "pointer" }}
                    onClick={() => {
                      toggleExpand(sourceKey);
                    }}
                  >
                    <TableCell>
                      <ExpandIcon expanded={expanded[sourceKey]}>
                        {expanded[sourceKey] ? "▼" : "▶"}
                      </ExpandIcon>
                      {sourceData.provider} - {sourceData.website}
                    </TableCell>
                    <TableCell>{sourceData.first_form_quotes}</TableCell>
                    <TableCell>
                      {sourceData.second_form_quotes === 0
                        ? 0
                        : (
                            (sourceData.first_form_quotes /
                              sourceData.second_form_quotes) *
                            100
                          ).toFixed(2)}
                      %
                    </TableCell>
                    <TableCell>{sourceData.cost.toFixed(2)}</TableCell>
                    <TableCell>{getRevenue(sourceData)}</TableCell>
                    <TableCell>
                      {(getRevenue(sourceData) - sourceData.cost).toFixed(2)}
                    </TableCell>

                    <TableCell>{getMarginOnCosts(sourceData)}%</TableCell>
                    <TableCell>
                      {sourceData.quotes_count === 0
                        ? 0
                        : (
                            getRevenue(sourceData) / sourceData.quotes_count
                          ).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      {sourceData.total_clicks === 0
                        ? 0
                        : (
                            getRevenue(sourceData) / sourceData.total_clicks
                          ).toFixed(4)}
                    </TableCell>
                    <TableCell>
                      {sourceData.quotes_count === 0
                        ? 0
                        : (
                            getRevenue(sourceData) / sourceData.quotes_count
                          ).toFixed(2)}
                    </TableCell>
                    <TableCell>{sourceData.total_clicks}</TableCell>
                    <TableCell>100.00%</TableCell>
                    <TableCell>{sourceData.quotes_count}</TableCell>
                    <TableCell>
                      {sourceData.total_clicks === 0 ||
                      sourceData.quotes_count === 0
                        ? 0
                        : (
                            sourceData.total_clicks / sourceData.quotes_count
                          ).toFixed(2)}
                    </TableCell>
                    <TableCell>
                      {sourceData.converted_salvage +
                        sourceData.converted_scrap}
                    </TableCell>
                    <TableCell>
                      {sourceData.converted_salvage +
                        sourceData.converted_scrap ===
                        0 || sourceData.total_clicks === 0
                        ? 0
                        : (
                            ((sourceData.converted_salvage +
                              sourceData.converted_scrap) /
                              sourceData.total_clicks) *
                            100
                          ).toFixed(2)}
                      %
                    </TableCell>
                    <TableCell>
                      {sourceData.converted_salvage +
                        sourceData.converted_scrap ===
                        0 || sourceData.quotes_count === 0
                        ? 0
                        : (
                            ((sourceData.converted_salvage +
                              sourceData.converted_scrap) /
                              sourceData.quotes_count) *
                            100
                          ).toFixed(2)}
                      %
                    </TableCell>
                    <TableCell>
                      {sourceData.quotes_count === 0 ||
                      sourceData.salvage_quotes === 0
                        ? 0
                        : (
                            (sourceData.salvage_quotes /
                              sourceData.quotes_count) *
                            100
                          ).toFixed(2)}
                      %
                    </TableCell>
                    <TableCell>
                      {sourceData.quotes_count === 0 ||
                      sourceData.no_cover_quotes === 0
                        ? 0
                        : (
                            (sourceData.no_cover_quotes /
                              sourceData.quotes_count) *
                            100
                          ).toFixed(2)}
                      %
                    </TableCell>
                    <TableCell>
                      {getScrapQuotePercentage(sourceData)}%
                    </TableCell>
                    <TableCell>{getHoursSpent(sourceData)}</TableCell>
                    <TableCell>
                      {getHoursSpent(sourceData) === 0
                        ? 0
                        : (
                            (getRevenue(sourceData) - sourceData.cost) /
                            getHoursSpent(sourceData)
                          ).toFixed(2)}
                    </TableCell>
                    <TableCell>{sourceData.salvage_quotes}</TableCell>
                    <TableCell>{sourceData.no_cover_quotes}</TableCell>
                    <TableCell>{sourceData.converted_scrap}</TableCell>
                    <TableCell>{getScrapConversion(sourceData)}%</TableCell>
                    <TableCell>{sourceData.converted_salvage}</TableCell>
                    <TableCell>{getSalvageConversion(sourceData)}%</TableCell>
                    <TableCell>{sourceData.total_impressions}</TableCell>
                    <TableCell>{sourceData.scrap_commission}</TableCell>
                    <TableCell>{sourceData.converted_salvage * 350}</TableCell>
                  </AnimatedRow>

                  {/* Campaign Level */}
                  {expanded[sourceKey] &&
                    Object.entries(sourceData.campaigns).map(
                      ([campaignKey, campaignData]) => (
                        <>
                          <AnimatedRow
                            key={campaignKey}
                            expanded={expanded[campaignKey]}
                            style={{
                              backgroundColor: "#f0f0f0",
                              cursor: "pointer",
                            }}
                            onClick={() => toggleExpand(campaignKey)}
                          >
                            <TableCell>
                              <ExpandIcon expanded={expanded[campaignKey]}>
                                {expanded[campaignKey] ? "▼" : "▶"}
                              </ExpandIcon>
                              {campaignData.campaign_name}
                            </TableCell>
                            <TableCell>
                              {campaignData.first_form_quotes}
                            </TableCell>
                            <TableCell>
                              {campaignData.second_form_quotes === 0
                                ? 0
                                : (
                                    (campaignData.first_form_quotes /
                                      campaignData.second_form_quotes) *
                                    100
                                  ).toFixed(2)}
                              %
                            </TableCell>
                            <TableCell>
                              {campaignData.cost.toFixed(2)}
                            </TableCell>
                            <TableCell>{getRevenue(campaignData)}</TableCell>
                            <TableCell>
                              {(
                                getRevenue(campaignData) - campaignData.cost
                              ).toFixed(2)}
                            </TableCell>
                            <TableCell>
                              {getMarginOnCosts(campaignData)}%
                            </TableCell>
                            <TableCell>
                              {campaignData.quotes_count === 0
                                ? 0
                                : (
                                    getRevenue(campaignData) /
                                    campaignData.quotes_count
                                  ).toFixed(2)}
                            </TableCell>
                            <TableCell>
                              {campaignData.total_clicks === 0
                                ? 0
                                : (
                                    getRevenue(campaignData) /
                                    campaignData.total_clicks
                                  ).toFixed(4)}
                            </TableCell>
                            <TableCell>
                              {campaignData.quotes_count === 0
                                ? 0
                                : (
                                    getRevenue(campaignData) /
                                    campaignData.quotes_count
                                  ).toFixed(2)}
                            </TableCell>
                            <TableCell>{campaignData.total_clicks}</TableCell>
                            <TableCell>
                              {sourceData.total_impressions === 0
                                ? "0.00%"
                                : (
                                    (campaignData.total_impressions /
                                      sourceData.total_impressions) *
                                    100
                                  ).toFixed(2) + "%"}
                            </TableCell>
                            <TableCell>{campaignData.quotes_count}</TableCell>
                            <TableCell>
                              {campaignData.total_clicks === 0 ||
                              campaignData.quotes_count === 0
                                ? 0
                                : (
                                    campaignData.total_clicks /
                                    campaignData.quotes_count
                                  ).toFixed(2)}
                            </TableCell>
                            <TableCell>
                              {campaignData.converted_salvage +
                                campaignData.converted_scrap}
                            </TableCell>
                            <TableCell>
                              {campaignData.converted_salvage +
                                campaignData.converted_scrap ===
                                0 || campaignData.total_clicks === 0
                                ? 0
                                : (
                                    ((campaignData.converted_salvage +
                                      campaignData.converted_scrap) /
                                      campaignData.total_clicks) *
                                    100
                                  ).toFixed(2)}
                              %
                            </TableCell>
                            <TableCell>
                              {campaignData.converted_salvage +
                                campaignData.converted_scrap ===
                                0 || campaignData.quotes_count === 0
                                ? 0
                                : (
                                    ((campaignData.converted_salvage +
                                      campaignData.converted_scrap) /
                                      campaignData.quotes_count) *
                                    100
                                  ).toFixed(2)}
                              %
                            </TableCell>
                            <TableCell>
                              {campaignData.quotes_count === 0 ||
                              campaignData.salvage_quotes === 0
                                ? 0
                                : (
                                    (campaignData.salvage_quotes /
                                      campaignData.quotes_count) *
                                    100
                                  ).toFixed(2)}
                              %
                            </TableCell>
                            <TableCell>
                              {campaignData.quotes_count === 0 ||
                              campaignData.no_cover_quotes === 0
                                ? 0
                                : (
                                    (campaignData.no_cover_quotes /
                                      campaignData.quotes_count) *
                                    100
                                  ).toFixed(2)}
                              %
                            </TableCell>
                            <TableCell>
                              {getScrapQuotePercentage(campaignData)}%
                            </TableCell>
                            <TableCell>{getHoursSpent(campaignData)}</TableCell>
                            <TableCell>
                              {getHoursSpent(campaignData) === 0
                                ? 0
                                : (
                                    (getRevenue(campaignData) -
                                      campaignData.cost) /
                                    getHoursSpent(campaignData)
                                  ).toFixed(2)}
                            </TableCell>
                            <TableCell>{campaignData.salvage_quotes}</TableCell>
                            <TableCell>
                              {campaignData.no_cover_quotes}
                            </TableCell>
                            <TableCell>
                              {campaignData.converted_scrap}
                            </TableCell>
                            <TableCell>
                              {getScrapConversion(campaignData)}%
                            </TableCell>
                            <TableCell>
                              {campaignData.converted_salvage}
                            </TableCell>
                            <TableCell>
                              {getSalvageConversion(campaignData)}%
                            </TableCell>
                            <TableCell>
                              {campaignData.total_impressions}
                            </TableCell>
                            <TableCell>
                              {campaignData.scrap_commission}
                            </TableCell>

                            <TableCell>
                              {campaignData.converted_salvage * 350}
                            </TableCell>
                          </AnimatedRow>

                          {/* Ad Group Level */}
                          {expanded[campaignKey] &&
                            campaignData.ad_groups.map((group) => (
                              <AnimatedRow
                                key={group.group_id}
                                expanded={true}
                                style={{ backgroundColor: "#e0e0e0" }}
                              >
                                <TableCell style={{ paddingLeft: "40px" }}>
                                  {group.group_name}
                                </TableCell>
                                <TableCell>{group.first_form_quotes}</TableCell>
                                <TableCell>
                                  {group.second_form_quotes === 0
                                    ? 0
                                    : (
                                        (group.first_form_quotes /
                                          group.second_form_quotes) *
                                        100
                                      ).toFixed(2)}
                                  %
                                </TableCell>
                                <TableCell>{group.cost.toFixed(2)}</TableCell>
                                <TableCell>{getRevenue(group)}</TableCell>
                                <TableCell>
                                  {(getRevenue(group) - group.cost).toFixed(2)}
                                </TableCell>
                                <TableCell>
                                  {getMarginOnCosts(group)}%
                                </TableCell>
                                <TableCell>
                                  {group.quotes_count === 0
                                    ? 0
                                    : (
                                        getRevenue(group) / group.quotes_count
                                      ).toFixed(2)}
                                </TableCell>
                                <TableCell>
                                  {group.total_clicks === 0
                                    ? 0
                                    : (
                                        getRevenue(group) / group.total_clicks
                                      ).toFixed(2)}
                                </TableCell>
                                <TableCell>
                                  {group.quotes_count === 0
                                    ? 0
                                    : (
                                        getRevenue(group) / group.quotes_count
                                      ).toFixed(2)}
                                </TableCell>
                                <TableCell>{group.total_clicks}</TableCell>
                                <TableCell>
                                  {campaignData.total_impressions === 0
                                    ? "0.00%"
                                    : (
                                        (group.total_impressions /
                                          campaignData.total_impressions) *
                                        100
                                      ).toFixed(2) + "%"}
                                </TableCell>
                                <TableCell>{group.quotes_count}</TableCell>
                                <TableCell>
                                  {group.total_clicks === 0 ||
                                  group.quotes_count === 0
                                    ? 0
                                    : (
                                        group.total_clicks / group.quotes_count
                                      ).toFixed(2)}
                                </TableCell>
                                <TableCell>
                                  {group.converted_salvage +
                                    group.converted_scrap}
                                </TableCell>
                                <TableCell>
                                  {group.converted_salvage +
                                    group.converted_scrap ===
                                    0 || group.total_clicks === 0
                                    ? 0
                                    : (
                                        ((group.converted_salvage +
                                          group.converted_scrap) /
                                          group.total_clicks) *
                                        100
                                      ).toFixed(2)}
                                  %
                                </TableCell>
                                <TableCell>
                                  {group.converted_salvage +
                                    group.converted_scrap ===
                                    0 || group.quotes_count === 0
                                    ? 0
                                    : (
                                        ((group.converted_salvage +
                                          group.converted_scrap) /
                                          group.quotes_count) *
                                        100
                                      ).toFixed(2)}
                                  %
                                </TableCell>
                                <TableCell>
                                  {group.quotes_count === 0 ||
                                  group.salvage_quotes === 0
                                    ? 0
                                    : (
                                        (group.salvage_quotes /
                                          group.quotes_count) *
                                        100
                                      ).toFixed(2)}
                                  %
                                </TableCell>
                                <TableCell>
                                  {group.quotes_count === 0 ||
                                  group.no_cover_quotes === 0
                                    ? 0
                                    : (
                                        (group.no_cover_quotes /
                                          group.quotes_count) *
                                        100
                                      ).toFixed(2)}
                                  %
                                </TableCell>
                                <TableCell>
                                  {getScrapQuotePercentage(group)}%
                                </TableCell>
                                <TableCell>{getHoursSpent(group)}</TableCell>
                                <TableCell>
                                  {getHoursSpent(campaignData) === 0
                                    ? 0
                                    : (
                                        (getMarginOnCosts(group) - group.cost) /
                                        getHoursSpent(group)
                                      ).toFixed(2)}
                                </TableCell>
                                <TableCell>{group.salvage_quotes}</TableCell>
                                <TableCell>{group.no_cover_quotes}</TableCell>
                                <TableCell>{group.converted_scrap}</TableCell>
                                <TableCell>
                                  {getScrapConversion(group)}%
                                </TableCell>
                                <TableCell>{group.converted_salvage}</TableCell>
                                <TableCell>
                                  {getSalvageConversion(group)}%
                                </TableCell>
                                <TableCell>{group.total_impressions}</TableCell>
                                <TableCell>{group.scrap_commission}</TableCell>

                                <TableCell>
                                  {group.converted_salvage * 350}
                                </TableCell>
                              </AnimatedRow>
                            ))}
                        </>
                      )
                    )}
                </>
              ))}
              <TableRow
                style={{ backgroundColor: "#bfffc9", fontWeight: "bold" }}
              >
                <TableCell>Total</TableCell>
                <TableCell>{totals.first_form_quotes}</TableCell>
                <TableCell>
                  {totals.second_form_quotes === 0
                    ? 0
                    : (
                        (totals.first_form_quotes / totals.second_form_quotes) *
                        100
                      ).toFixed(2)}
                  %
                </TableCell>
                <TableCell>{totals.cost.toFixed(2)}</TableCell>
                <TableCell>{getRevenue(totals)}</TableCell>
                <TableCell>
                  {(getRevenue(totals) - totals.cost).toFixed(2)}
                </TableCell>
                <TableCell>
                  {totals.cost === 0
                    ? 0
                    : (
                        ((getRevenue(totals) - totals.cost) / totals.cost) *
                        100
                      ).toFixed(2)}
                  %
                </TableCell>
                <TableCell>
                  {totals.quotes_count === 0
                    ? 0
                    : (getRevenue(totals) / totals.quotes_count).toFixed(2)}
                </TableCell>
                <TableCell>
                  {totals.total_clicks === 0
                    ? 0
                    : (getRevenue(totals) / totals.total_clicks).toFixed(4)}
                </TableCell>
                <TableCell>
                  {totals.quotes_count === 0
                    ? 0
                    : (getRevenue(totals) / totals.quotes_count).toFixed(2)}
                </TableCell>
                <TableCell>{totals.total_clicks}</TableCell>
                <TableCell>100.00%</TableCell>
                <TableCell>{totals.quotes_count}</TableCell>
                <TableCell>
                  {totals.total_clicks === 0 || totals.quotes_count === 0
                    ? 0
                    : (totals.total_clicks / totals.quotes_count).toFixed(2)}
                </TableCell>
                <TableCell>
                  {totals.converted_salvage + totals.converted_scrap}
                </TableCell>
                <TableCell>
                  {totals.converted_salvage + totals.converted_scrap === 0 ||
                  totals.total_clicks === 0
                    ? 0
                    : (
                        ((totals.converted_salvage + totals.converted_scrap) /
                          totals.total_clicks) *
                        100
                      ).toFixed(2)}
                  %
                </TableCell>
                <TableCell>
                  {totals.converted_salvage + totals.converted_scrap === 0 ||
                  totals.quotes_count === 0
                    ? 0
                    : (
                        ((totals.converted_salvage + totals.converted_scrap) /
                          totals.quotes_count) *
                        100
                      ).toFixed(2)}
                  %
                </TableCell>
                <TableCell>
                  {totals.quotes_count === 0 || totals.salvage_quotes === 0
                    ? 0
                    : (
                        (totals.quotes_count / totals.salvage_quotes) *
                        100
                      ).toFixed(2)}
                  %
                </TableCell>
                <TableCell>
                  {totals.quotes_count === 0 || totals.no_cover_quotes === 0
                    ? 0
                    : (
                        (totals.quotes_count / totals.no_cover_quotes) *
                        100
                      ).toFixed(2)}
                  %
                </TableCell>
                <TableCell>{getScrapQuotePercentage(totals)}%</TableCell>
                <TableCell>{getHoursSpent(totals)}</TableCell>
                <TableCell>
                  {getHoursSpent(totals) === 0
                    ? 0
                    : (
                        (getRevenue(totals) - totals.cost) /
                        getHoursSpent(totals)
                      ).toFixed(2)}
                </TableCell>
                <TableCell>{totals.salvage_quotes}</TableCell>
                <TableCell>{totals.no_cover_quotes}</TableCell>
                <TableCell>{totals.converted_scrap}</TableCell>
                <TableCell>{getScrapConversion(totals)}%</TableCell>
                <TableCell>{totals.converted_salvage}</TableCell>
                <TableCell>{getSalvageConversion(totals)}%</TableCell>
                <TableCell>{totals.total_impressions}</TableCell>
                <TableCell>{totals.scrap_commission}</TableCell>
                <TableCell>{totals.converted_salvage * 350}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        ) : null}
        {reportResponse.length === 0 && !isLoading ? (
          <Alert icon={false} severity="info">
            Please select a date and search for results.
          </Alert>
        ) : null}
      </Card>
    </CardContent>
  );
}
